import Transition from "@shared/core/Transition";
import theme from "@shared/utils/themeColors";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import FeedbackPanel from "../modals/FeedbackPanel";
import ModalBlank from "../modals/ModalBlank";

const LegalDocIcon = () => (
  <svg
    className="w-2.5 h-3 fill-current shrink-0 mr-2"
    viewBox="0 0 10 12"
  >
    <path d="M6 0H2C.9 0 0 .9 0 2v8c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V4L6 0zm1.5 9.5h-5v-1h5v1zm0-2h-5v-1h5v1zM5.5 4.5V1l3.5 3.5H6c-.28 0-.5-.22-.5-.5z" />
  </svg>
);

function DropdownHelp({ align }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <>
      <div className="relative inline-flex">
        <button
          ref={trigger}
          className={`w-8 h-8 flex items-center justify-center hover:bg-gray-100 lg:hover:bg-gray-200 dark:hover:bg-gray-700/50 dark:lg:hover:bg-gray-800 rounded-full ${
            dropdownOpen && "bg-gray-200 dark:bg-gray-800"
          }`}
          aria-haspopup="true"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          aria-expanded={dropdownOpen}
        >
          <span className="sr-only">Need help?</span>
          <svg
            className="fill-current text-gray-500/80 dark:text-gray-400/80"
            width={16}
            height={16}
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9 7.5a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0v-4ZM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
            <path
              fillRule="evenodd"
              d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm6-8A6 6 0 1 1 2 8a6 6 0 0 1 12 0Z"
            />
          </svg>
        </button>

        <Transition
          className={`origin-top-right z-10 absolute top-full min-w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
            align === "right" ? "right-0" : "left-0"
          }`}
          show={dropdownOpen}
          enter="transition ease-out duration-200 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
        >
          <div
            ref={dropdown}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setDropdownOpen(false)}
          >
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase pt-1.5 pb-2 px-3">
              Need help?
            </div>
            <ul>
              <li>
                <button
                  className={`font-medium text-sm ${theme.text.link} flex items-center py-1 px-3 w-full text-left`}
                  onClick={() => {
                    setDropdownOpen(false);
                    setFeedbackModalOpen(true);
                  }}
                >
                  <svg
                    className="w-3 h-3 fill-current shrink-0 mr-2"
                    viewBox="0 0 12 12"
                  >
                    <path d="M10.5 0h-9A1.5 1.5 0 000 1.5v9A1.5 1.5 0 001.5 12h9a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0010.5 0zM10 7L8.207 5.207l-3 3-1.414-1.414 3-3L5 2h5v5z" />
                  </svg>
                  <span>Give Feedback</span>
                </button>
              </li>
              <li>
                <Link
                  className={`font-medium text-sm ${theme.text.link} flex items-center py-1 px-3`}
                  to="https://www.globalcca.org/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDropdownOpen(false)}
                >
                  <svg
                    className="w-3 h-3 fill-current shrink-0 mr-2"
                    viewBox="0 0 12 12"
                  >
                    <path d="M11.854.146a.5.5 0 00-.525-.116l-11 4a.5.5 0 00-.015.934l4.8 1.921 1.921 4.8A.5.5 0 007.5 12h.008a.5.5 0 00.462-.329l4-11a.5.5 0 00-.116-.525z" />
                  </svg>
                  <span>Contact us</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`font-medium text-sm ${theme.text.link} flex items-center py-1 px-3`}
                  to="/faqs"
                  onClick={() => setDropdownOpen(false)}
                >
                  <svg
                    className="w-3 h-3 fill-current shrink-0 mr-2"
                    viewBox="0 0 12 12"
                  >
                    <path d="M6 0a6 6 0 100 12A6 6 0 006 0zm0 10a1 1 0 110-2 1 1 0 010 2zm1-3.5v.5a1 1 0 01-2 0v-1a1 1 0 011-1 .5.5 0 10-.5-.5 1 1 0 01-2 0A2.5 2.5 0 116 7z" />
                  </svg>
                  <span>FAQs</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`font-medium text-sm ${theme.text.link} flex items-center py-1 px-3`}
                  to="/privacy-policy"
                  onClick={() => setDropdownOpen(false)}
                >
                  <LegalDocIcon />
                  <span>Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link
                  className={`font-medium text-sm ${theme.text.link} flex items-center py-1 px-3`}
                  to="/terms-and-conditions"
                  onClick={() => setDropdownOpen(false)}
                >
                  <LegalDocIcon />
                  <span>Terms & Conditions</span>
                </Link>
              </li>
            </ul>
          </div>
        </Transition>
      </div>

      <ModalBlank
        id="feedback-modal"
        modalOpen={feedbackModalOpen}
        setModalOpen={setFeedbackModalOpen}
      >
        <FeedbackPanel setModalOpen={setFeedbackModalOpen} />
      </ModalBlank>
    </>
  );
}

export default DropdownHelp;
